import React, { useState, useEffect, useCallback } from "react";
import "./leave.css";
import { toast } from "react-toastify";
import { applyLeave, getMyLeaves } from "../../service/project_service";
import { getProfile } from "../../service/auth_service";
import DataTable from "../../components/custom_data_table";
import CustomLoadingindicator from "../../components/custom_loading";

const Leave = () => {
  const [formData, setFormData] = useState({
    employeename: "",
    leaveType: "",
    startDate: "",
    endDate: "",
    totalDays: "",
    reason: "",
  });

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [profileRes, leavesRes] = await Promise.all([
        getProfile(),
        getMyLeaves(),
      ]);

      if (profileRes?.status === "success") {
        setFormData((prev) => ({
          ...prev,
          employeename: profileRes.data?.employeename || "",
        }));
      }

      if (leavesRes?.status === "success") {
        const reports = leavesRes.data || [];
        const formattedLeaves = reports.map((leave, index) => ({
          ...leave,
          sno: index + 1,
          displayLeaveType: leave.leavetype || leave.leave_type || "-",
          displayStartDate: leave.startdate || leave.start_date || "-",
          displayEndDate: leave.enddate || leave.end_date || "-",
          displayTotalDays: leave.totaldays || leave.total_days || 0,
        }));
        setLeaves(formattedLeaves);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch initial data");
    } finally {
      setLoading(false);
    }
  };

  const { startDate, endDate } = formData;

  const calculateDays = useCallback(() => {
    if (!startDate || !endDate) {
      setFormData((prev) => ({ ...prev, totalDays: "" }));
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      setFormData((prev) => ({ ...prev, totalDays: "0" }));
      return;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
    setFormData((prev) => ({ ...prev, totalDays: diffDays.toString() }));
  }, [startDate, endDate]);

  useEffect(() => {
    calculateDays();
  }, [calculateDays]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error("End date cannot be before start date");
      return;
    }

    try {
      setSubmitLoading(true);
      const payload = {
        leavetype: formData.leaveType,
        startdate: formData.startDate,
        enddate: formData.endDate,
        totaldays: Number(formData.totalDays),
        reason: formData.reason,
      };

      const response = await applyLeave(payload);

      if (response.status === "success") {
        toast.success(response.message || "Leave applied successfully");
        setFormData((prev) => ({
          ...prev,
          leaveType: "",
          startDate: "",
          endDate: "",
          totalDays: "",
          reason: "",
        }));
        const leavesRes = await getMyLeaves();
        if (leavesRes?.status === "success") {
          const reports = leavesRes.data || [];
          const formattedLeaves = reports.map((leave, index) => ({
            ...leave,
            sno: index + 1,
            displayLeaveType: leave.leavetype || leave.leave_type || "-",
            displayStartDate: leave.startdate || leave.start_date || "-",
            displayEndDate: leave.enddate || leave.end_date || "-",
            displayTotalDays: leave.totaldays || leave.total_days || 0,
          }));
          setLeaves(formattedLeaves);
        }
      } else {
        toast.error(response.message || "Failed to apply leave");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const leaveColumns = [
    {
      key: "sno",
      label: "S.No",
    },
    {
      key: "employeename",
      label: "Name",
    },
    {
      key: "displayStartDate",
      label: "Start Date",
      render: (value) =>
        value && value !== "-"
          ? new Date(value).toLocaleDateString("en-GB")
          : "-",
    },
    {
      key: "displayEndDate",
      label: "End Date",
      render: (value) =>
        value && value !== "-"
          ? new Date(value).toLocaleDateString("en-GB")
          : "-",
    },
    {
      key: "displayTotalDays",
      label: "Total Days",
    },
    {
      key: "reason",
      label: "Reason",
    },
    {
      key: "status",
      label: "Status",
      render: (value) => (
        <span
          style={{
            color:
              value === "Approved"
                ? "#2e7d32"
                : value === "Rejected"
                  ? "#d32f2f"
                  : "#ed6c02",
            fontWeight: "600",
            padding: "4px 8px",
            borderRadius: "4px",
            backgroundColor:
              value === "Approved"
                ? "#e8f5e9"
                : value === "Rejected"
                  ? "#ffeeeb"
                  : "#fff3e0",
            fontSize: "12px",
          }}
        >
          {value || "Pending"}
        </span>
      ),
    },
  ];

  if (loading) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="leave-container">
      <h4>Apply Leave</h4>
      <div className="leave-card">
        <form className="leave-form" onSubmit={handleSubmit}>
          <div className="leave-form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.employeename}
              readOnly
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </div>

          <div className="leave-form-group">
            <label>Leave Type *</label>
            <select
              name="leaveType"
              value={formData.leaveType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Leave Type</option>
              <option value="Casual Leave">Casual Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Loss of Pay">Loss of Pay</option>
            </select>
          </div>

          <div className="leave-form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="leave-form-group">
            <label>End Date *</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="leave-form-group">
            <label>Total Days</label>
            <input
              type="text"
              name="totalDays"
              value={formData.totalDays}
              readOnly
              style={{ backgroundColor: "#f5f5f5" }}
            />
          </div>

          <div className="leave-form-group leave-form-group-full">
            <label>Reason *</label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows="4"
              placeholder="Enter reason for leave..."
              required
            />
          </div>

          <div className="submit-buttons leave-form-group-full">
            <button
              type="submit"
              className="apply-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Loading..." : "Submit"}
            </button>
            <button
              type="button"
              className="reset-btn"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  leaveType: "",
                  startDate: "",
                  endDate: "",
                  totalDays: "",
                  reason: "",
                }))
              }
              disabled={submitLoading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      <div style={{ height: 30 }}></div>

      <h4>My Leaves</h4>
      <DataTable
        columns={leaveColumns}
        data={leaves}
        loading={loading}
        pageSize={5}
      />
    </div>
  );
};

export default Leave;
